import { FC, SyntheticEvent, useMemo, useRef } from 'react';
import { faBed, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { checkAHAVI } from 'utils/utils';
import {
  getClientClaudeContent,
  getHotelInfoImageUrl,
  resolveHotelInfo,
} from 'utils/shared/hotelProposalInformation';
import { TripPreviewHotelCard, TripPreviewHotelRoomInfo } from 'utils/shared/tripResultBuilderPreview';
import { dateFormat, i18nTB } from 'utils/shared/utils';

type TripBuilderClientHotelDetails2Props = {
  card: TripPreviewHotelCard;
  selectedRoomIndex: number;
  productNote?: string;
  onSelectRoom?: (roomIndex: number) => void;
  onClose?: () => void;
};

export const formatFreeCancellationLabel = (
  cxlLabel: string,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  const match = cxlLabel.match(/(\d{1,2}\s+[A-Za-z]+\s+\d{4}|\d{1,2}[./]\d{1,2}[./]\d{2,4})/);
  if (!match?.[1]) {
    return cxlLabel;
  }

  let displayDate = match[1];
  const parsed = new Date(match[1]);
  if (!Number.isNaN(parsed.getTime())) {
    displayDate = dateFormat(parsed.toISOString(), 'dd.MM.yy', locale);
  }

  return t('trip_builder.free_cancellation_until', { date: displayDate });
};

const formatRoomCxlLabel = (
  room: TripPreviewHotelRoomInfo,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (!room.cxlLabel) {
    return null;
  }

  if (room.cxlType === 'free') {
    return formatFreeCancellationLabel(room.cxlLabel, locale, t);
  }

  if (room.cxlType === 'full') {
    return t('trip_builder.full_cancellation_fee');
  }

  return room.cxlLabel;
};

const HGST02_SUPPLIER_CODE = 'hgst02';
const HGST02_MAX_IMAGES = 15;

const getHgst02HotelImageUrls = (hotelInformatiom?: { images?: Array<{ imageLink?: string }> }) => {
  const urls: string[] = [];

  hotelInformatiom?.images?.forEach((image) => {
    if (urls.length >= HGST02_MAX_IMAGES) {
      return;
    }

    const url = getHotelInfoImageUrl(image)?.trim();
    if (url && !urls.includes(url)) {
      urls.push(url);
    }
  });

  return urls;
};

export const getTripBuilderHotelImageUrls = (card: TripPreviewHotelCard, i18nLang: string) => {
  if (card.supplierCode?.toLowerCase() === HGST02_SUPPLIER_CODE) {
    return getHgst02HotelImageUrls(card.hotelInformatiom);
  }

  const urls: string[] = [];
  const addUrl = (url?: string | null) => {
    const normalized = url?.trim();
    if (normalized && !urls.includes(normalized)) {
      urls.push(normalized);
    }
  };

  const hotelInfo = resolveHotelInfo(card.hotelInformatiom);
  hotelInfo?.images?.forEach((image: any) => addUrl(getHotelInfoImageUrl(image)));

  const claudeContent = getClientClaudeContent(card.aI_HotelInformatiom?.content, i18nLang);
  claudeContent?.images?.forEach((image) => addUrl(image.link));

  card.serpHotel?.images?.forEach((image: { originalImage?: string }) =>
    addUrl(image.originalImage),
  );

  return urls;
};

const TripBuilderClientHotelDetails2: FC<TripBuilderClientHotelDetails2Props> = ({
  card,
  selectedRoomIndex,
  productNote,
  onSelectRoom,
  onClose,
}) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const rooms = card.rooms || [];
  const hasMultipleRooms = rooms.length > 1;
  const hasRoomInfo = rooms.some((room) => room.roomLabel || room.boardLabel || room.cxlLabel);

  const imageUrls = useMemo(
    () => getTripBuilderHotelImageUrls(card, i18n.language),
    [card, i18n.language],
  );

  const imagesRef = useRef<HTMLDivElement>(null);

  const onImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.closest('.tb2-hotel-details-image')?.remove();

    if (imagesRef.current && !imagesRef.current.children.length) {
      imagesRef.current.remove();
    }
  };

  return (
    <div className="tb2-hotel-details">
      <button type="button" className="tb2-hotel-toggle" onClick={onClose}>
        <span className="tb2-hotel-toggle-label">
          {t('close')}
          <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
        </span>
        <span className="tb2-hotel-toggle-line" aria-hidden="true" />
      </button>

      {!!productNote && (
        <div
          className={'tb2-hotel-details-overview' + (checkAHAVI(productNote) ? ' tb2-hotel-details-rtl' : '')}
          dangerouslySetInnerHTML={{ __html: productNote.replaceAll(/\n/g, '<br />') }}
        />
      )}

      {hasRoomInfo && (
        <div className="tb2-hotel-details-rooms" dir="ltr">
          {rooms.map((room, roomIndex) => {
            const cxlLabel = formatRoomCxlLabel(room, i18n.language, t);

            return (
              <div
                className={
                  'tb2-hotel-details-room-row' +
                  (hasMultipleRooms ? ' tb2-hotel-details-room-row-clickable' : '') +
                  (hasMultipleRooms && selectedRoomIndex === roomIndex
                    ? ' tb2-hotel-details-room-row-selected'
                    : '')
                }
                key={roomIndex}
                onClick={() => {
                  if (hasMultipleRooms) {
                    onSelectRoom?.(roomIndex);
                  }
                }}
              >
                {hasMultipleRooms && (
                  <span
                    className={
                      'tb2-hotel-details-room-checkbox' +
                      (selectedRoomIndex === roomIndex ? ' tb2-hotel-details-room-checkbox-active' : '')
                    }
                    aria-hidden="true"
                  />
                )}
                {!!room.roomLabel && (
                  <div className="tb2-hotel-details-room-name">
                    <FontAwesomeIcon icon={faBed} aria-hidden="true" />
                    <span>{room.roomLabel}</span>
                  </div>
                )}
                {!!room.boardLabel && (
                  <span className="tb2-hotel-details-room-tag tb2-hotel-details-room-tag-board text-cap">
                    {room.boardLabel}
                  </span>
                )}
                {!!cxlLabel && (
                  <span
                    className={
                      'tb2-hotel-details-room-tag tb2-hotel-details-room-tag-cxl' +
                      (room.cxlType === 'free'
                        ? ' tb2-hotel-details-room-tag-cxl-free'
                        : room.cxlType === 'full'
                          ? ' tb2-hotel-details-room-tag-cxl-full'
                          : '')
                    }
                  >
                    {cxlLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!!imageUrls.length && (
        <div className="tb2-hotel-details-images" ref={imagesRef}>
          {imageUrls.map((url, index) => (
            <div className="tb2-hotel-details-image" key={`${url}-${index}`}>
              <img src={url} alt="" loading="lazy" onError={onImageError} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TripBuilderClientHotelDetails2;
